// src/components/tutorials/JS_Function_Definitions.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_Definitions() {
  const refs = {
    declaration: useRef(null),
    expression: useRef(null),
    arrow: useRef(null),
    anonymous: useRef(null),
    iife: useRef(null),
    constructor: useRef(null),
    nested: useRef(null),
    defaultParams: useRef(null),
    restParams: useRef(null),
    hoisting: useRef(null),
    callback: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const keyPoints = [
    { key: "declaration", label: "Function Declaration" },
    { key: "expression", label: "Function Expression" },
    { key: "arrow", label: "Arrow Function" },
    { key: "anonymous", label: "Anonymous Function" },
    { key: "iife", label: "Immediately Invoked Function Expression (IIFE)" },
    { key: "constructor", label: "Function Constructor" },
    { key: "nested", label: "Nested Functions" },
    { key: "defaultParams", label: "Default Parameters" },
    { key: "restParams", label: "Rest Parameters" },
    { key: "hoisting", label: "Function Hoisting" },
    { key: "callback", label: "Callback Functions" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚡ JavaScript Function Definitions</h1>
      <p className="tutorial-intro">
        JavaScript provides multiple ways to define functions. This tutorial
        covers all major function definition techniques along with examples.
      </p>

      {/* Two-column Key Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1.5rem 2.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
          marginBottom: "3rem",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.5rem",
          }}
        >
          {keyPoints.map((item) => (
            <p key={item.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(item.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {item.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* Sections */}

      {/* 1. Function Declaration */}
      <div ref={refs.declaration} className="tutorial-section">
        <h3>1️⃣ Function Declaration</h3>
        <p>
          Defines a named function. Function declarations are hoisted and can be
          called before they are defined.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet(name) {
  return "Hello " + name;
}

console.log(greet("Alice")); // Hello Alice`}
          />
          <TryItButton
            code={`function greet(name){return "Hello "+name;}console.log(greet("Alice"));`}
          />{" "}
        </div>
        <OutputBox>Output: Hello Alice</OutputBox>
      </div>

      {/* 2. Function Expression */}
      <div ref={refs.expression} className="tutorial-section">
        <h3>2️⃣ Function Expression</h3>
        <p>
          Assigns a function (named or anonymous) to a variable. Function
          expressions are not hoisted.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const greet = function(name) {
  return "Hello " + name;
};

console.log(greet("Bob")); // Hello Bob`}
          />

          <TryItButton
            code={`const greet=function(name){return "Hello "+name;};console.log(greet("Bob"));`}
          />
        </div>
        <OutputBox>Output: Hello Bob</OutputBox>
      </div>

      {/* 3. Arrow Function */}
      <div ref={refs.arrow} className="tutorial-section">
        <h3>3️⃣ Arrow Function</h3>
        <p>
          Shorter syntax introduced in ES6. Arrow functions do not have their
          own <code className="highlight_code">this</code>.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const greet = (name) => "Hello " + name;

console.log(greet("Charlie")); // Hello Charlie`}
          />
          <TryItButton
            code={`const greet=(name)=>"Hello "+name;console.log(greet("Charlie"));`}
          />{" "}
        </div>
        <OutputBox>Output: Hello Charlie</OutputBox>
      </div>

      {/* 4. Anonymous Function */}
      <div ref={refs.anonymous} className="tutorial-section">
        <h3>4️⃣ Anonymous Function</h3>
        <p>
          Function without a name, often used as arguments to other functions.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`setTimeout(function() {
  console.log("Hello after 1 second");
}, 1000);`}
          />

          <TryItButton
            code={`setTimeout(function(){console.log("Hello after 1 second");},1000);`}
          />
        </div>
        <OutputBox>Output: Hello after 1 second</OutputBox>
      </div>

      {/* 5. IIFE */}
      <div ref={refs.iife} className="tutorial-section">
        <h3>5️⃣ Immediately Invoked Function Expression (IIFE)</h3>
        <p>A function that runs immediately after it is defined.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`(function() {
  console.log("IIFE executed");
})();`}
          />

          <TryItButton
            code={`(function(){console.log("IIFE executed");})();`}
          />
        </div>
        <OutputBox>Output: IIFE executed</OutputBox>
      </div>

      {/* 6. Function Constructor */}
      <div ref={refs.constructor} className="tutorial-section">
        <h3>6️⃣ Function Constructor</h3>
        <p>
          Functions can be created using the{" "}
          <code className="highlight_code">Function</code> constructor.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const add = new Function("a", "b", "return a + b;");
console.log(add(5, 3)); // 8`}
          />
          <TryItButton
            code={`const add=new Function("a","b","return a+b;");console.log(add(5,3));`}
          />{" "}
        </div>
        <OutputBox>Output: 8</OutputBox>
      </div>

      {/* 7. Nested Functions */}
      <div ref={refs.nested} className="tutorial-section">
        <h3>7️⃣ Nested Functions</h3>
        <p>
          Functions can be defined inside other functions and have access to the
          outer function’s scope.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function outer(x) {
  function inner(y) {
    return x + y;
  }
  return inner(5);
}

console.log(outer(10)); // 15`}
          />
          <TryItButton
            code={`function outer(x){function inner(y){return x+y;}return inner(5);}console.log(outer(10));`}
          />{" "}
        </div>
        <OutputBox>Output: 15</OutputBox>
      </div>

      {/* 8. Default Parameters */}
      <div ref={refs.defaultParams} className="tutorial-section">
        <h3>8️⃣ Default Parameters</h3>
        <p>
          Functions can have default values for parameters if no argument is
          provided.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet(name = "Guest") {
  return "Hello " + name;
}

console.log(greet()); // Hello Guest`}
          />

          <TryItButton
            code={`function greet(name="Guest"){return "Hello "+name;}console.log(greet());`}
          />
        </div>
        <OutputBox>Output: Hello Guest</OutputBox>
      </div>

      {/* 9. Rest Parameters */}
      <div ref={refs.restParams} className="tutorial-section">
        <h3>9️⃣ Rest Parameters</h3>
        <p>
          Allows a function to accept an indefinite number of arguments as an
          array.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function sum(...nums) {
  return nums.reduce((a,b) => a + b, 0);
}

console.log(sum(1,2,3,4)); // 10`}
          />
          <TryItButton
            code={`function sum(...nums){return nums.reduce((a,b)=>a+b,0);}console.log(sum(1,2,3,4));`}
          />{" "}
        </div>
        <OutputBox>Output: 10</OutputBox>
      </div>

      {/* 10. Function Hoisting */}
      <div ref={refs.hoisting} className="tutorial-section">
        <h3>🔟 Function Hoisting</h3>
        <p>
          Function declarations are hoisted to the top of their scope, so they
          can be called before they appear in the code.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(add(5,3)); // 8

function add(a,b){
  return a + b;
}`}
          />
          <TryItButton
            code={`console.log(add(5,3));function add(a,b){return a+b;}`}
          />{" "}
        </div>
        <OutputBox>Output: 8</OutputBox>
      </div>

      {/* 11. Callback Functions */}
      <div ref={refs.callback} className="tutorial-section">
        <h3>1️⃣1️⃣ Callback Functions</h3>
        <p>
          A function passed as an argument to another function and executed
          later.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet(name, callback) {
  console.log("Hello " + name);
  callback();
}

greet("Alice", function() {
  console.log("Callback executed");
});`}
          />

          <TryItButton
            code={`function greet(name,callback){console.log("Hello "+name);callback();}greet("Alice",function(){console.log("Callback executed");});`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>Hello Alice Callback executed</pre>
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Try combining different function types in real projects.</li>
          <li>
            Learn about <code className="highlight_code">this</code> behavior in
            arrow vs normal functions.
          </li>
          <li>Understand higher-order functions for functional programming.</li>
        </ul>
      </div>
    </div>
  );
}
